import React, { useMemo, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
    Alert,
    Box,
    Typography,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableHead,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    LinearProgress,
} from '@mui/material';

import MapIcon from '@mui/icons-material/Map';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';

import { useAppSelector, useAppDispatch } from 'hooks';
import { BFMCManager, WorkflowManager } from 'models';
import { GuideActions } from 'state';

import Page from 'components/Page';
import BFMCDetails from 'components/BFMCDetails';
import PageFooter from 'components/PageFooter';
import { toTimeDDMonYearFormat } from 'utils';
import {
    BfWorkshopId,
    FaftWorkshopId,
    BfWorkflowId,
    FaftWorkflowId,
} from 'models/workflow';
import { ConfirmContextWrapper } from 'components/ConfirmationDialog';
import {
    getBmcDisplay,
    getStatusPalette,
    getWorkshopProgress,
} from 'utils/bmcDisplay';

interface PageProps {}

const PlanPage: React.FunctionComponent<PageProps> = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { bfmcId } = useParams<{ bfmcId?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();

    const {
        auth: { user },
        guide,
    } = useAppSelector((s) => s);

    const bfmcs = useMemo(
        () =>
            guide.BFMCs.object?.filter((a) =>
                (user.object?.BFMCs?.findIndex((b) => b.id === a.id) ?? -1) > -1
                    ? a
                    : false,
            ),
        [user.object, guide.BFMCs.object],
    );

    const planType = guide.selectedPlanType ?? 'bfrmp';
    const isBfrmp = planType === 'bfrmp';

    const onSelectBFMC = (bfmc: BFMCManager.BFMC | null) => {
        dispatch(GuideActions.setSelectedBFMC(bfmc));
    };

    const onMapClick = (bfmc: BFMCManager.BFMC | null) => {
        if (!bfmc) return;
        onSelectBFMC(bfmc);
        navigate('/gis');
    };

    // Temporary FAFT entry point: `/plan?type=faft` flips selectedPlanType
    // (and `?type=bfrmp` flips back). The param is stripped after consumption
    // so the URL stays canonical; the chosen type is persisted via the
    // setSelectedPlanType reducer's localStorage write.
    useEffect(() => {
        const requested = searchParams.get('type');
        if (requested == null) return;
        const next = new URLSearchParams(searchParams);
        next.delete('type');
        if (
            (requested === 'bfrmp' || requested === 'faft') &&
            requested !== guide.selectedPlanType
        ) {
            dispatch(GuideActions.setSelectedPlanType(requested));
        }
        setSearchParams(next, { replace: true });
    }, [searchParams]);

    // Hydrate selectedBFMC from URL (option b: URL drives selection).
    // Also handles the legacy single-BMC auto-select for users with one assignment.
    useEffect(() => {
        if (!bfmcs) return;

        if (bfmcId != null) {
            const target = bfmcs.find((b) => String(b.id) === bfmcId) ?? null;
            if (target?.id !== guide.selectedBFMC?.id) onSelectBFMC(target);
            return;
        }

        // No URL id — clear selection so the list shows
        if (guide.selectedBFMC != null) onSelectBFMC(null);

        // Auto-select for BFRMP users with exactly one BMC assignment
        if (isBfrmp && bfmcs.length === 1) {
            navigate(`/plan/${bfmcs[0].id}`, { replace: true });
        }
    }, [bfmcs, bfmcId]);

    const closeDetails = () => navigate('/plan');

    // Derive the target BMC synchronously from URL + accessible BFMCs.
    // Avoids depending on guide.selectedBFMC (set by an effect, one render late).
    const targetBmc =
        bfmcId != null && bfmcs != null
            ? (bfmcs.find((b) => String(b.id) === bfmcId) ?? null)
            : null;

    // Spin while auth or BFMCs are still in flight so the filter is reliable.
    const awaitingUrlBmc =
        bfmcId != null &&
        (user.status === 'loading' ||
            user.status === 'idle' ||
            guide.BFMCs.status === 'loading' ||
            guide.BFMCs.status === 'idle');

    return (
        <Page>
            {awaitingUrlBmc ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                    <CircularProgress />
                </Box>
            ) : targetBmc != null ? (
                <ConfirmContextWrapper>
                    <BFMCDetails
                        bfmc={targetBmc}
                        onClose={closeDetails}
                        onMapClick={() => onMapClick(targetBmc)}
                        loading={false}
                    />
                </ConfirmContextWrapper>
            ) : (
                <Box
                    sx={{
                        position: 'relative',
                        backgroundColor: '#2B1520',
                        overflow: 'hidden',
                        mx: -1,
                        my: -1,
                        minHeight: 'calc(100vh - 80px)',
                    }}
                >
                    {/* Background circle artwork - top right */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -200,
                            right: -200,
                            width: 800,
                            height: 800,
                            backgroundImage: 'url(/hero-contours.png)',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'contain',
                            opacity: 0.08,
                            pointerEvents: 'none',
                            zIndex: 1,
                        }}
                    />

                    <Box
                        sx={{
                            maxWidth: 1280,
                            mx: 'auto',
                            px: { xs: 2, sm: 3 },
                            pt: { xs: 4, sm: 6, md: 8 },
                            pb: { xs: 4, sm: 6 },
                            position: 'relative',
                            zIndex: 10,
                        }}
                    >
                        {bfmcId != null && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                You don&apos;t have permission to access this
                                BMA.
                            </Alert>
                        )}
                        <Typography
                            variant="h4"
                            sx={{
                                mb: { xs: 3, sm: 4 },
                                fontSize: {
                                    xs: '1.125rem',
                                    sm: '1.25rem',
                                    md: '1.5rem',
                                },
                                fontWeight: 500,
                                color: '#FFFFFF',
                                textAlign: { xs: 'center', sm: 'left' },
                            }}
                        >
                            {isBfrmp
                                ? 'Select a Bushfire Management Committee'
                                : 'Select a Bushfire Management Committee (FAFT)'}
                        </Typography>

                        {/* Loading state */}
                        {(user.status === 'loading' ||
                            user.status === 'idle' ||
                            ((guide.BFMCs.status === 'loading' ||
                                guide.BFMCs.status === 'idle') &&
                                guide.selectedBFMC == null)) && (
                            <Box
                                sx={{
                                    display: 'grid',
                                    placeItems: 'center',
                                    py: 8,
                                    gap: 2,
                                }}
                            >
                                <CircularProgress
                                    size={32}
                                    sx={{ color: '#FFFFFF' }}
                                />
                                <Typography
                                    sx={{ color: 'rgba(255,255,255,0.85)' }}
                                >
                                    {user.status === 'loading' ||
                                    user.status === 'idle'
                                        ? 'Loading user profile'
                                        : 'Loading plan details'}
                                </Typography>
                            </Box>
                        )}

                        {/* Empty state */}
                        {bfmcs && bfmcs.length === 0 && (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    py: 6,
                                    color: '#FFFFFF',
                                    textAlign: 'center',
                                }}
                            >
                                <Typography variant="h6">
                                    No Bushfire Management Committee Assigned.
                                </Typography>
                                <Typography
                                    sx={{ color: 'rgba(255,255,255,0.85)' }}
                                >
                                    Contact your local XO to be assigned to a
                                    BMC.
                                </Typography>
                            </Box>
                        )}

                        {user.status === 'finished' &&
                            guide.BFMCs.status === 'finished' &&
                            bfmcs &&
                            bfmcs.length > 0 && (
                                <TableContainer
                                    component={Paper}
                                    sx={{
                                        backgroundColor: '#FFFFFF',
                                        border: '4px solid white',
                                        borderRadius: 2,
                                        boxShadow:
                                            '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                                        transition: 'box-shadow 0.3s ease',
                                        overflowX: 'auto',
                                        '&:hover': {
                                            boxShadow:
                                                '0 20px 50px rgba(0,0,0,0.2)',
                                        },
                                    }}
                                >
                                    <Table
                                        sx={{
                                            minWidth: {
                                                xs: 600,
                                                sm: 800,
                                                md: 'auto',
                                            },
                                        }}
                                    >
                                        <TableHead>
                                            <TableRow
                                                sx={{
                                                    backgroundColor:
                                                        'rgba(249, 250, 251, 0.6)',
                                                    borderBottom:
                                                        '2px solid rgba(229, 231, 235, 0.6)',
                                                }}
                                            >
                                                <TableCell
                                                    sx={{
                                                        width: { sm: '25%' },
                                                        color: '#374151',
                                                        textTransform:
                                                            'uppercase',
                                                        letterSpacing: '0.05em',
                                                    }}
                                                >
                                                    Bushfire Management
                                                    Committee
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        width: { sm: '22%' },
                                                        color: '#374151',
                                                        display: {
                                                            xs: 'none',
                                                            md: 'table-cell',
                                                        },
                                                        textTransform:
                                                            'uppercase',
                                                        letterSpacing: '0.05em',
                                                    }}
                                                >
                                                    Workshop
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        width: { sm: '22%' },
                                                        color: '#374151',
                                                        textTransform:
                                                            'uppercase',
                                                        letterSpacing: '0.05em',
                                                    }}
                                                >
                                                    Status
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        width: { sm: '13%' },
                                                        color: '#374151',
                                                        display: {
                                                            xs: 'none',
                                                            lg: 'table-cell',
                                                        },
                                                        textTransform:
                                                            'uppercase',
                                                        letterSpacing: '0.05em',
                                                    }}
                                                >
                                                    Last Updated
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        width: { sm: '18%' },
                                                        color: '#374151',
                                                        textTransform:
                                                            'uppercase',
                                                        letterSpacing: '0.05em',
                                                    }}
                                                >
                                                    Actions
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {bfmcs.map((bfmc, index) => {
                                                const currentPlan =
                                                    bfmc.plans.filter(
                                                        (plan) =>
                                                            plan.plan_type ===
                                                            planType,
                                                    )[0];
                                                if (!currentPlan) return null;

                                                const stage =
                                                    WorkflowManager.getWorkflow(
                                                        planType,
                                                        currentPlan.workshop_status,
                                                    );
                                                const workshop = isBfrmp
                                                    ? WorkflowManager
                                                          .bfWorkshops[
                                                          stage.workshopId as BfWorkshopId
                                                      ]
                                                    : WorkflowManager
                                                          .faftWorkshops[
                                                          stage.workshopId as FaftWorkshopId
                                                      ];
                                                const date =
                                                    currentPlan.updates.find(
                                                        (t) =>
                                                            t.to === stage.id,
                                                    )?.date;

                                                const display = getBmcDisplay(
                                                    bfmc.code,
                                                );
                                                const progress =
                                                    getWorkshopProgress(
                                                        planType,
                                                        currentPlan.workshop_status as
                                                            | BfWorkflowId
                                                            | FaftWorkflowId,
                                                    );
                                                const palette =
                                                    getStatusPalette(
                                                        progress.statusType,
                                                    );

                                                return (
                                                    <TableRow
                                                        key={bfmc.id}
                                                        sx={{
                                                            borderBottom:
                                                                index ===
                                                                bfmcs.length - 1
                                                                    ? 'none'
                                                                    : '1px solid rgba(229, 231, 235, 0.5)',
                                                            transition:
                                                                'background-color 0.2s ease',
                                                            position:
                                                                'relative',
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    'rgba(255, 255, 255, 0.4)',
                                                            },
                                                        }}
                                                    >
                                                        <TableCell
                                                            sx={{
                                                                position:
                                                                    'relative',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    position:
                                                                        'absolute',
                                                                    left: 0,
                                                                    top: 0,
                                                                    bottom: 0,
                                                                    width: 6,
                                                                    backgroundColor:
                                                                        display.accentColour,
                                                                    borderRadius:
                                                                        '0 4px 4px 0',
                                                                    boxShadow:
                                                                        '0 1px 3px 0 rgba(0,0,0,0.1)',
                                                                }}
                                                            />
                                                            <Box
                                                                sx={{
                                                                    pl: 3,
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                }}
                                                            >
                                                                <Box
                                                                    component="button"
                                                                    onClick={() =>
                                                                        navigate(
                                                                            `/plan/${bfmc.id}`,
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        background:
                                                                            'none',
                                                                        border: 'none',
                                                                        p: 0,
                                                                        cursor: 'pointer',
                                                                        transition:
                                                                            'all 0.2s ease',
                                                                        '&:hover':
                                                                            {
                                                                                opacity: 0.8,
                                                                                transform:
                                                                                    'scale(1.05)',
                                                                            },
                                                                    }}
                                                                    title={
                                                                        bfmc.name
                                                                    }
                                                                >
                                                                    <Box
                                                                        component="img"
                                                                        src={
                                                                            display.logoSrc
                                                                        }
                                                                        alt={
                                                                            bfmc.name
                                                                        }
                                                                        sx={{
                                                                            height: {
                                                                                xs: 48,
                                                                                sm: 56,
                                                                                md: 64,
                                                                            },
                                                                            width: 'auto',
                                                                            objectFit:
                                                                                'contain',
                                                                            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        </TableCell>

                                                        <TableCell
                                                            sx={{
                                                                color: 'text.primary',
                                                                display: {
                                                                    xs: 'none',
                                                                    md: 'table-cell',
                                                                },
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    fontSize: {
                                                                        xs: '0.8rem',
                                                                        sm: '0.875rem',
                                                                    },
                                                                }}
                                                            >
                                                                {workshop.name}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell>
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    gap: 1.5,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        flexShrink: 0,
                                                                        pt: 0.5,
                                                                    }}
                                                                >
                                                                    {progress.statusType ===
                                                                    'finalised' ? (
                                                                        <CheckCircleIcon
                                                                            sx={{
                                                                                fontSize: 28,
                                                                                color: palette.fg,
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <PlayCircleFilledIcon
                                                                            sx={{
                                                                                fontSize: 28,
                                                                                color: palette.fg,
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        flexDirection:
                                                                            'column',
                                                                        gap: 0.75,
                                                                        flex: 1,
                                                                        minWidth: 0,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: palette.fg,
                                                                            fontWeight: 700,
                                                                            fontSize:
                                                                                {
                                                                                    xs: '0.8rem',
                                                                                    sm: '0.875rem',
                                                                                },
                                                                            lineHeight: 1.3,
                                                                        }}
                                                                    >
                                                                        {
                                                                            progress.statusLabel
                                                                        }
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: 'text.secondary',
                                                                            fontSize:
                                                                                {
                                                                                    xs: '0.7rem',
                                                                                    sm: '0.75rem',
                                                                                },
                                                                            lineHeight: 1.3,
                                                                        }}
                                                                    >
                                                                        {progress.isNotStarted
                                                                            ? 'Not started'
                                                                            : `Stage ${progress.stageInWorkshop}/${progress.stagesInWorkshop}`}
                                                                    </Typography>
                                                                    <LinearProgress
                                                                        variant="determinate"
                                                                        value={
                                                                            (progress.stageInWorkshop /
                                                                                progress.stagesInWorkshop) *
                                                                            100
                                                                        }
                                                                        sx={{
                                                                            height: 8,
                                                                            borderRadius: 4,
                                                                            backgroundColor:
                                                                                '#e5e7eb',
                                                                            '& .MuiLinearProgress-bar':
                                                                                {
                                                                                    backgroundColor:
                                                                                        palette.fg,
                                                                                    borderRadius: 4,
                                                                                },
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        </TableCell>

                                                        <TableCell
                                                            sx={{
                                                                color: 'text.secondary',
                                                                display: {
                                                                    xs: 'none',
                                                                    lg: 'table-cell',
                                                                },
                                                                fontSize: {
                                                                    xs: '0.8rem',
                                                                    sm: '0.875rem',
                                                                },
                                                            }}
                                                        >
                                                            {date
                                                                ? toTimeDDMonYearFormat(
                                                                      date,
                                                                  )
                                                                : 'N/A'}
                                                        </TableCell>

                                                        <TableCell>
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    gap: 1,
                                                                    alignItems:
                                                                        'center',
                                                                }}
                                                            >
                                                                <Button
                                                                    onClick={() =>
                                                                        navigate(
                                                                            `/plan/${bfmc.id}`,
                                                                        )
                                                                    }
                                                                    variant="text"
                                                                    sx={{
                                                                        color: '#666',
                                                                        textTransform:
                                                                            'uppercase',
                                                                        fontSize:
                                                                            '0.75rem',
                                                                        fontWeight: 700,
                                                                        letterSpacing:
                                                                            '0.05em',
                                                                        px: 2,
                                                                        py: 1,
                                                                        minWidth:
                                                                            'auto',
                                                                        transition:
                                                                            'all 0.2s ease',
                                                                        '&:hover':
                                                                            {
                                                                                color: '#374151',
                                                                                backgroundColor:
                                                                                    'rgba(0, 0, 0, 0.04)',
                                                                                transform:
                                                                                    'translateY(-2px)',
                                                                            },
                                                                    }}
                                                                >
                                                                    Details
                                                                </Button>
                                                                <Button
                                                                    onClick={() =>
                                                                        onMapClick(
                                                                            bfmc,
                                                                        )
                                                                    }
                                                                    variant="text"
                                                                    startIcon={
                                                                        <MapIcon
                                                                            sx={{
                                                                                fontSize:
                                                                                    '1rem',
                                                                            }}
                                                                        />
                                                                    }
                                                                    sx={{
                                                                        color: '#666',
                                                                        textTransform:
                                                                            'uppercase',
                                                                        fontSize:
                                                                            '0.75rem',
                                                                        fontWeight: 700,
                                                                        letterSpacing:
                                                                            '0.05em',
                                                                        px: 2,
                                                                        py: 1,
                                                                        minWidth:
                                                                            'auto',
                                                                        whiteSpace:
                                                                            'nowrap',
                                                                        transition:
                                                                            'all 0.2s ease',
                                                                        '&:hover':
                                                                            {
                                                                                color: '#374151',
                                                                                backgroundColor:
                                                                                    'rgba(0, 0, 0, 0.04)',
                                                                                transform:
                                                                                    'translateY(-2px)',
                                                                            },
                                                                    }}
                                                                >
                                                                    View Map
                                                                </Button>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                    </Box>
                    <PageFooter />
                </Box>
            )}
        </Page>
    );
};

PlanPage.displayName = 'PlanPage';
export default PlanPage;
